import { QueryFunction } from '@tanstack/react-query';
import {
  FirestoreError,
  onSnapshot,
  Query,
  QuerySnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { getErrorReporter } from 'shared/services/error-tracking';
import { ID } from 'shared/types/id';
import { isProductionEnvironment } from 'shared/utils/is-environment';

import { queryClient } from './query-client';

/**
 * Creates a TanStack Query function that establishes real-time Firestore listeners.
 * * Improvements over original:
 * 1. Uses context.signal to automatically unsubscribe when the query becomes stale or unmounts.
 * 2. Uses a Set to track initialization, preventing race conditions where one query fires multiple times before others fire once.
 * 3. Handles pruning of stale data correctly across multiple query sources.
 */
export const createSubscriptionQuery = <T extends { id: ID }>(
  queries: (() => Promise<Query<T>>)[],
): QueryFunction<T[] | undefined, string[]> => {
  return async (context) => {
    const { signal, queryKey } = context;
    if (signal?.aborted) {
      throw new Error('Query aborted before execution');
    }

    getErrorReporter().addBreadcrumb({
      category: 'tanstack-query',
      message: JSON.stringify(queryKey),
    });

    // Resolve the query references (this might involve async logic to build the query)
    const queryRefs = await Promise.all(queries.map((query) => query()));

    // State Tracking
    let firstRun = true;
    const combinedDataMap = new Map<ID, T>();
    // Tracks the specific IDs returned by each query index so we can prune stale items
    const queryIdSets: Set<ID>[] = queries.map(() => new Set<ID>());
    // Tracks which queries have fired their initial payload (from Cache or Server)
    const initializedQueries = new Set<number>();

    // 🆕 Track retry counts per query index to prevent infinite loops
    const retryCounts = new Array(queries.length).fill(0);
    const MAX_RETRIES = 3;

    // Store unsubs by index so we can replace them individually
    const unsubscribers: Unsubscribe[] = new Array(queries.length);

    return new Promise<T[] | undefined>((resolve, reject) => {
      // triggers when the user navigates away or the query key changes.
      const cleanup = () => {
        unsubscribers.forEach((unsub) => unsub?.());
      };

      signal?.addEventListener('abort', () => {
        cleanup();
        // if initialization hasn't finished, reject to prevent hanging promises
        if (firstRun) {
          reject(new Error('Query aborted by React Query'));
        }
      });

      const updateCacheAndResolve = () => {
        // Prune stale items
        const allActiveIds = new Set(
          queryIdSets.flatMap((set) => Array.from(set)),
        );
        for (const [id] of combinedDataMap) {
          if (!allActiveIds.has(id)) {
            combinedDataMap.delete(id);
          }
        }

        const combinedData = Array.from(combinedDataMap.values());

        if (firstRun) {
          // Only resolve once ALL queries have initialized successfully
          if (initializedQueries.size === queryRefs.length) {
            firstRun = false;
            resolve(combinedData);
          }
          return;
        }

        // Subsequent updates: Manually update the React Query cache to keep the UI in sync with real-time changes
        queryClient.setQueryData(queryKey, combinedData);
      };

      const setupSingleSubscription = (index: number) => {
        const queryRef = queryRefs[index];

        const onSnapshotSuccess = (snapshot: QuerySnapshot<T>) => {
          // Only reset retry count on server-confirmed success.
          // Cached snapshots fire alongside server errors (e.g. missing index),
          // and resetting here would prevent the counter from ever reaching MAX_RETRIES.
          if (!snapshot.metadata.fromCache) {
            retryCounts[index] = 0;
          }

          const currentIds = new Set<ID>();
          snapshot.docs.forEach((doc) => {
            const data = doc.data();
            combinedDataMap.set(data.id, data);
            currentIds.add(data.id);
          });

          queryIdSets[index] = currentIds;
          initializedQueries.add(index);

          updateCacheAndResolve();
        };

        const onSnapshotError = (error: FirestoreError) => {
          if (!isProductionEnvironment) {
            const errorContext = `index=${index} retry=${retryCounts[index]} at ${performance.now().toFixed(0)}ms`;
            // eslint-disable-next-line no-console -- non-prod logging
            console.warn(
              `[Debug-Subscription] ERROR query ${JSON.stringify(queryKey)} ${errorContext}`,
              error,
            );
          }
          // cleanup THIS specific listener (it's dead anyway, but good hygiene)
          unsubscribers[index]?.();

          // create a new listener for this specific query if we haven't reached the max retries
          if (retryCounts[index] < MAX_RETRIES) {
            retryCounts[index]++;
            const delay = 1000 * retryCounts[index]; // Exponential backoff: 1s, 2s, 3s...

            // wait, then try to subscribe again
            setTimeout(() => {
              // Ensure we haven't been aborted in the meantime
              if (!signal?.aborted) {
                setupSingleSubscription(index);
              }
            }, delay);
            return;
          }

          if (!isProductionEnvironment) {
            // eslint-disable-next-line no-console -- non-prod logging
            console.log('Firestore Query Error', { queryKey, index, error });
          }

          getErrorReporter().captureException(error, {
            tags: {
              source: 'firestore-subscription-query',
              queryKey: JSON.stringify(queryKey),
              queryIndex: String(index),
            },
            contexts: {
              subscriptionQuery: { queryKey, index },
            },
          });

          // if max tries have been reached, time for cleanup and rejection
          if (firstRun) {
            cleanup();
            reject(error);
            return;
          }
          // Set the query to error state so the UI can react accordingly.
          // Don't use invalidateQueries — it re-executes the queryFn,
          // resetting retry counters in a new closure and creating an infinite loop.
          cleanup();
          const cachedQuery = queryClient
            .getQueryCache()
            .getAll()
            .find(
              (query) =>
                JSON.stringify(query.queryKey) === JSON.stringify(queryKey),
            );
          if (cachedQuery) {
            // query.setState is not documented by TanStack Query but is used
            // internally to set error state. There is no public API for this.
            cachedQuery.setState({
              status: 'error',
              error,
              fetchStatus: 'idle',
            });
          }
        };

        // connect and store the specific unsub function
        unsubscribers[index] = onSnapshot(
          queryRef,
          onSnapshotSuccess,
          onSnapshotError,
        );
      };

      try {
        queryRefs.forEach((_, index) => setupSingleSubscription(index));
      } catch (error) {
        if (firstRun) {
          cleanup();
          reject(error);
        }
      }
    });
  };
};
